import { ICustomLeadParser, ResourceAutoCompleteViewModel, ResourceAutoCompleteViewModelType } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { ParserType } from '../../../extViewmodels/sdk/models/LeadParser';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { IEventLoggingComponentProps, withEventLogging } from '../../../models/Logging';
import { AccountOperationsViewModel } from '../../../viewmodels/AdminViewModels';
import { baseStyleSheet } from '../../styles/styles';
import { Checkbox } from '../Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../ConfirmationDialog';
import { HelpTooltip } from '../HelpTooltip';
import { LoadingSpinner } from '../LoadingSpinner';
import { asModalComponent } from '../Modal';
import { RadioButton } from '../RadioButton';
import { WarningIcon } from '../svgs/icons/WarningIcon';
import { TextInput } from '../TextInput';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../autocomplete/SimpleAutoCompleteSearchField';
import { styleSheet } from './styles';

interface IProps extends IModalContext<ICustomLeadParser>, IEventLoggingComponentProps {
	isProcessing?: boolean;
	parser?: ICustomLeadParser;
	onCancel(): void;
	onDelete?(parser: ICustomLeadParser): void;
	onSave(parser: ICustomLeadParser, isNew: boolean): void;
	tagVM?: AccountOperationsViewModel;
}

const idPrefix = 'custom-lead-parser';
const defaultParser: ICustomLeadParser = {
	birthdayField: '',
	emailField: '',
	firstNameField: '',
	isDisabled: false,
	keyFactField: '',
	lastNameField: '',
	name: '',
	nameField: '',
	phoneField: '',
	preferHtml: false,
	splitTagFieldByDelimiter: false,
	splitKeyFactFieldByDelimiter: false,
	senderEmail: '',
	subject: '',
	tag: '',
	tagField: '',
	aiOptions: {
		enabled: false,
	},
};

const deleteConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const BaseCustomLeadParserModal: React.FC<IProps> = observer(
	({ isProcessing = false, logInput, onCancel, onDelete, onSave, parser: p, tagVM }) => {
		const [parser, setParser] = useState(p ?? defaultParser);
		const [processing, setProcessing] = useState(isProcessing);
		const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

		const isContentGenerationEnabled = tagVM?.account?.features?.contentGeneration?.enabled === true;
		const initialParserType = p?.aiOptions?.enabled && isContentGenerationEnabled ? ParserType.AI : ParserType.Custom;
		const [parserType, setParserType] = useState<ParserType>(initialParserType);

		const isNew = !p;

		const nameId = `${idPrefix}-name`;
		const senderEmailId = `${idPrefix}-sender-email`;
		const subjectId = `${idPrefix}-subject`;
		const tagNameId = `${idPrefix}-tag-name`;
		const emailFieldId = `${idPrefix}-email-field`;
		const nameFieldId = `${idPrefix}-name-field`;
		const firstNameFieldId = `${idPrefix}-firstname-field`;
		const lastNameFieldId = `${idPrefix}-lastname-field`;
		const phoneFieldId = `${idPrefix}-phone-field`;
		const preferHtmlId = `${idPrefix}-prefer-html`;
		const splitTagFieldByDelimiterFieldId = `${idPrefix}-split-tag-field-by-delimiter`;
		const splitKeyFactFieldByDelimiterFieldId = `${idPrefix}-split-key-fact-field-by-delimiter`;
		const tagFieldId = `${idPrefix}-tag-field`;
		const keyFactFieldId = `${idPrefix}-keyFact-field`;
		const birthdayFieldId = `${idPrefix}-birthday-field`;
		const addressLine1FieldId = `${idPrefix}-address-line1-field`;
		const addressLine2FieldId = `${idPrefix}-address-line2-field`;
		const addressStateFieldId = `${idPrefix}-address-state-field`;
		const addressCityFieldId = `${idPrefix}-address-city-field`;
		const addressPostalCodeFieldId = `${idPrefix}-address-postal-code-field`;
		useEffect(() => {
			setProcessing(isProcessing);
		}, [isProcessing]);

		useEffect(() => {
			const newType = p?.aiOptions?.enabled && isContentGenerationEnabled ? ParserType.AI : ParserType.Custom;
			setParserType(newType);
		}, [p, isContentGenerationEnabled]);

		const onDisabledChange = (parserIsDisabled: boolean) => () => {
			setParser({ ...parser, isDisabled: parserIsDisabled });
		};

		const onFieldChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
			switch (field) {
				case nameId:
					setParser({ ...parser, name: e.target.value });
					break;
				case senderEmailId:
					setParser({ ...parser, senderEmail: e.target.value });
					break;
				case subjectId:
					setParser({ ...parser, subject: e.target.value });
					break;
				case emailFieldId:
					setParser({ ...parser, emailField: e.target.value });
					break;
				case firstNameFieldId:
					setParser({ ...parser, firstNameField: e.target.value });
					break;
				case lastNameFieldId:
					setParser({ ...parser, lastNameField: e.target.value });
					break;
				case nameFieldId:
					setParser({ ...parser, nameField: e.target.value });
					break;
				case phoneFieldId:
					setParser({ ...parser, phoneField: e.target.value });
					break;
				case tagFieldId:
					setParser({ ...parser, tagField: e.target.value });
					break;
				case keyFactFieldId:
					setParser({ ...parser, keyFactField: e.target.value });
					break;
				case birthdayFieldId:
					setParser({ ...parser, birthdayField: e.target.value });
					break;
				case addressLine1FieldId:
					setParser({ ...parser, addressLine1Field: e.target.value });
					break;
				case addressLine2FieldId:
					setParser({ ...parser, addressLine2Field: e.target.value });
					break;
				case addressCityFieldId:
					setParser({ ...parser, cityField: e.target.value });
					break;
				case addressStateFieldId:
					setParser({ ...parser, stateField: e.target.value });
					break;
				case addressPostalCodeFieldId:
					setParser({ ...parser, postalCodeField: e.target.value });
					break;
				default:
					break;
			}
		};

		const onPreferHtmlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setParser({ ...parser, preferHtml: e.target.checked });
		};

		const onSplitTagFieldByDelimiterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setParser({ ...parser, splitTagFieldByDelimiter: e.target.checked });
		};

		const onSplitKeyFactFieldByDelimiterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setParser({ ...parser, splitKeyFactFieldByDelimiter: e.target.checked });
		};

		const onParserTypeChange = (type: ParserType) => () => {
			if (type === ParserType.AI && !isContentGenerationEnabled) {
				return;
			}
			setParserType(type);
			setParser(prevParser => ({
				...prevParser,
				aiOptions: {
					...(prevParser.aiOptions || {}),
					enabled: type === ParserType.AI,
				},
			}));
		};

		const onParserCancel = () => {
			setParser(defaultParser);
			onCancel();
		};

		const onParserDelete = () => {
			logInput('Delete', 'Click', { parserId: parser?.id });
			setIsDeleteConfirmationOpen(true);
		};

		const onDeleteConfirmationClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			if (result?.isDestructive && !canceled) {
				onDelete?.(parser);
			}
			setIsDeleteConfirmationOpen(false);
		};

		const onParserSave = () => {
			logInput('Save', 'Click', { parserId: parser?.id });
			onSave(parser, isNew);
		};
		const onTagSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string>) => {
			setParser({ ...parser, tag: e.selection });
		};

		const onClearTag = () => {
			setParser({ ...parser, tag: null });
		};
		const onCreateAutoCompleteViewModel = (
			_: ResourceAutoCompleteViewModelType,
			suggestedVm: ResourceAutoCompleteViewModel
		) => {
			return suggestedVm.impersonate({ account: tagVM.account });
		};
		const onSearchFieldChanged = (e: ISimpleAutoCompleteSearchFieldEvent<React.ChangeEvent<HTMLInputElement>>) => {
			if (e.sourceEvent) {
				const inputElement = e.sourceEvent.target as HTMLInputElement;
				setParser({ ...parser, tag: inputElement.value || '' });
			}
		};

		const onSearchFieldKeyDown = (e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
			if (!!e.sourceEvent && e.sourceEvent.keyCode === 13) {
				const inputElement = e.sourceEvent.target as HTMLInputElement;
				setParser({ ...parser, tag: inputElement.value || '' });
				inputElement.blur();
			}
		};

		return (
			<div className={css(styleSheet.container)}>
				<h1 className={css(styleSheet.title)}>Manage lead source</h1>
				<div className={css(styleSheet.body)}>
					<p className={css(styleSheet.text, styleSheet.bodyText)}>
						Custom lead sources allow you to configure a lead parser just for one customer. The most common use case is
						a form on the customer&apos;s web site. We will scan all emails with the From email address below and the
						subject line and use our AI to try to parse the name and email address in the form, then we&apos;ll tag the
						contact with the tag you enter below.
					</p>
					<div className={css(styleSheet.inputWrapper)}>
						<label className={css(styleSheet.text, styleSheet.label)}>Parser Type</label>
						<div className={css(baseStyleSheet.flex, styleSheet.radioButtonGroup)}>
							<RadioButton
								checked={parserType === ParserType.Custom}
								id={uuid()}
								name='parser-type'
								onChange={onParserTypeChange(ParserType.Custom)}
							>
								<div>Custom</div>
							</RadioButton>
							<div className={css(baseStyleSheet.flex, styleSheet.aiOptionWrapper)}>
								<RadioButton
									checked={parserType === ParserType.AI}
									disabled={!isContentGenerationEnabled}
									id={uuid()}
									name='parser-type'
									onChange={onParserTypeChange(ParserType.AI)}
									className={css(styleSheet.aiRadioButton)}
								>
									<div className={!isContentGenerationEnabled ? css(styleSheet.disabledText) : ''}>AI</div>
								</RadioButton>
								{!isContentGenerationEnabled && (
									<span className={css(styleSheet.tooltipWrapper)}>
										<HelpTooltip className={css(styleSheet.smallQuestionIcon)} preferredPlacement={['right', 'top']}>
											<div className={css(styleSheet.tooltipContent)}>
												This feature requires Content Generation to be enabled for your account.
											</div>
										</HelpTooltip>
									</span>
								)}
							</div>
						</div>
					</div>
					{parserType === ParserType.Custom && (
						<p className={css(styleSheet.text, styleSheet.bodyText, baseStyleSheet.flex)}>
							Limiting to a specific user
							<HelpTooltip className={css(styleSheet.tooltip)}>
								<div className={css(styleSheet.tooltipContent)}>
									To limit a custom lead parser to a specific user:
									<br />
									<br />
									<ul>
										<li>- Update the form to send to userid@forward.levitate.ai</li>
										<li>
											- Clear <b>Sender&apos;s Email Address</b> and <b>Email Subject Line</b>
										</li>
									</ul>
								</div>
							</HelpTooltip>
						</p>
					)}
					<div className={css(styleSheet.spacer)} />
					{!isNew && (
						<div className={css(styleSheet.inputWrapper, styleSheet.isDisabledContainer)}>
							<RadioButton
								checked={!parser.isDisabled}
								id={uuid()}
								name='custom-lead-parser-enabled'
								onChange={onDisabledChange(false)}
							>
								<div>Enable</div>
							</RadioButton>
							<RadioButton
								checked={parser.isDisabled}
								id={uuid()}
								name='custom-lead-parser-enabled'
								onChange={onDisabledChange(true)}
							>
								<div>Disable</div>
							</RadioButton>
						</div>
					)}
					<div className={css(styleSheet.inputWrapper)}>
						<label htmlFor={nameId} className={css(styleSheet.text, styleSheet.label)}>
							Integration Name
						</label>
						<TextInput inputId={nameId} onChange={onFieldChange(nameId)} type='text' value={parser.name} />
					</div>
					<div className={css(styleSheet.inputWrapper)}>
						<label htmlFor={senderEmailId} className={css(styleSheet.text, styleSheet.label)}>
							Sender&apos;s Email Address
						</label>
						<TextInput
							inputId={senderEmailId}
							onChange={onFieldChange(senderEmailId)}
							type='text'
							value={parser.senderEmail}
						/>
					</div>
					<div className={css(styleSheet.inputWrapper)}>
						<label htmlFor={subjectId} className={css(styleSheet.text, styleSheet.label)}>
							Email Subject Line
						</label>
						<TextInput inputId={subjectId} onChange={onFieldChange(subjectId)} type='text' value={parser.subject} />
					</div>
					<div className={css(styleSheet.inputWrapper)}>
						<label htmlFor={tagNameId} className={css(styleSheet.text, styleSheet.label)}>
							Tag Name
						</label>
						<SimpleAutoCompleteSearchField
							initialSearchQuery={parser.tag || ''}
							onChange={onSearchFieldChanged}
							onClear={onClearTag}
							onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
							// @ts-ignore
							onItemSelected={onTagSelected}
							onKeyDown={onSearchFieldKeyDown}
							pageSize={3}
							placeholder='Search existing or enter new tag'
							resultsLimit={3}
							type={ResourceAutoCompleteViewModelType.Tag}
						/>
					</div>
					{parserType === ParserType.Custom && (
						<>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={emailFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Email Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the email by default. Levitate will
												automatically find fields like &quot;Primary Email&quot;, &quot;Email Address&quot;, or
												&quot;Email&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={emailFieldId}
									onChange={onFieldChange(emailFieldId)}
									type='text'
									value={parser.emailField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={nameFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Name Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the name by default. Levitate will
												automatically find fields like &quot;Name&quot;, or &quot;Customer Name&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={nameFieldId}
									onChange={onFieldChange(nameFieldId)}
									type='text'
									value={parser.nameField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={firstNameFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										First Name Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the first name by default. Levitate will
												automatically find &quot;First Name&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={firstNameFieldId}
									onChange={onFieldChange(firstNameFieldId)}
									type='text'
									value={parser.firstNameField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={lastNameFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Last Name Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the last name by default. Levitate will
												automatically find &quot;Last Name&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={lastNameFieldId}
									onChange={onFieldChange(lastNameFieldId)}
									type='text'
									value={parser.lastNameField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={phoneFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Phone Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the phone by default. Levitate will
												automatically find fields like &quot;Phone Number&quot;, &quot;Primary Phone&quot;, &quot;Home
												Phone&quot;, or just &quot;Phone&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={phoneFieldId}
									onChange={onFieldChange(phoneFieldId)}
									type='text'
									value={parser.phoneField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								{/* Flex row: Label on left, Checkbox on right */}
								<div className={css(styleSheet.inlineCheckboxContainer)}>
									{/* Left side: Tag Field label + tooltip */}
									<label htmlFor={tagFieldId} className={css(styleSheet.text, styleSheet.label)}>
										<div className={css(baseStyleSheet.flex)}>
											Tag Field
											<HelpTooltip className={css(styleSheet.tooltip)}>
												<div className={css(styleSheet.tooltipContent)}>
													Only supply a value here if Levitate can&apos;t pick up a tag by default. Levitate will
													automatically find fields like &quot;Tag&quot;, &quot;Tags&quot;. You can supply a comma
													separate value to look for multiple fields.
												</div>
											</HelpTooltip>
										</div>
									</label>

									{/* Right side: Split by Delimiter checkbox (inline, only for Custom parsers) */}
									{parserType === ParserType.Custom && (
										<div className={css(baseStyleSheet.flex)}>
											<Checkbox
												id={splitTagFieldByDelimiterFieldId}
												onChange={onSplitTagFieldByDelimiterChange}
												checked={parser.splitTagFieldByDelimiter}
											>
												Split by Delimiter
											</Checkbox>
											<HelpTooltip className={css(styleSheet.inlineTooltip)}>
												<div className={css(styleSheet.tooltipContent)}>
													When enabled, comma or semicolon-separated tag values will be split into individual tags.
													<br />
													<br />
													<strong>Example:</strong> &quot;Flatbed, Reefer, Dry Van&quot; becomes three separate tags.
													<br />
													<br />
													<strong>Supported delimiters:</strong> Comma (,) and Semicolon (;)
												</div>
											</HelpTooltip>
										</div>
									)}
								</div>

								{/* TextInput stays below (unchanged) */}
								<TextInput
									inputId={tagFieldId}
									onChange={onFieldChange(tagFieldId)}
									type='text'
									value={parser.tagField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								{/* Flex row: Label on left, Checkbox on right */}
								<div className={css(styleSheet.inlineCheckboxContainer)}>
									{/* Left side: Key Fact Field label + tooltip */}
									<label htmlFor={keyFactFieldId} className={css(styleSheet.text, styleSheet.label)}>
										<div className={css(baseStyleSheet.flex)}>
											Key Fact Field
											<HelpTooltip className={css(styleSheet.tooltip)}>
												<div className={css(styleSheet.tooltipContent)}>
													Only supply a value here if Levitate can&apos;t pick up a key fact by default. Levitate will
													automatically find fields like &quot;Key Fact&quot;, &quot;Key Facts&quot;. You can supply a
													comma separate value to look for multiple fields.
												</div>
											</HelpTooltip>
										</div>
									</label>

									{/* Right side: Split by Delimiter checkbox (inline, only for Custom parsers) */}
									{parserType === ParserType.Custom && (
										<div className={css(baseStyleSheet.flex)}>
											<Checkbox
												id={splitKeyFactFieldByDelimiterFieldId}
												onChange={onSplitKeyFactFieldByDelimiterChange}
												checked={parser.splitKeyFactFieldByDelimiter}
											>
												Split by Delimiter
											</Checkbox>
											<HelpTooltip className={css(styleSheet.inlineTooltip)}>
												<div className={css(styleSheet.tooltipContent)}>
													When enabled, comma or semicolon-separated key fact values will be split into individual key
													facts.
													<br />
													<br />
													<strong>Example:</strong> &quot;Auto, Home, Life&quot; becomes three separate key facts.
													<br />
													<br />
													<strong>Supported delimiters:</strong> Comma (,) and Semicolon (;)
												</div>
											</HelpTooltip>
										</div>
									)}
								</div>

								{/* TextInput stays below (unchanged) */}
								<TextInput
									inputId={keyFactFieldId}
									onChange={onFieldChange(keyFactFieldId)}
									type='text'
									value={parser.keyFactField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={birthdayFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Birthday Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												If supplied, Levitate will look for a birthday in the email. You can supply a comma separate
												value to look for multiple fields. Only one birthday will be captured.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={birthdayFieldId}
									onChange={onFieldChange(birthdayFieldId)}
									type='text'
									value={parser.birthdayField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={addressLine1FieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Address Line 1 Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the address line by default. Levitate
												will automatically find fields like &quot;Address Line 1&quot;, &quot;Street Address&quot;,
												&quot;Street&quot;, or &quot;Street 1&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={addressLine1FieldId}
									onChange={onFieldChange(addressLine1FieldId)}
									type='text'
									value={parser.addressLine1Field}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={addressLine2FieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Address Line 2 Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the address line by default. Levitate
												will automatically find fields like &quot;Address Line 2&quot; or &quot;Street 2&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={addressLine2FieldId}
									onChange={onFieldChange(addressLine2FieldId)}
									type='text'
									value={parser.addressLine2Field}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={addressCityFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Address City Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the address city by default. Levitate
												will automatically find &quot;City&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={addressCityFieldId}
									onChange={onFieldChange(addressCityFieldId)}
									type='text'
									value={parser.cityField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={addressStateFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Address State Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the address state by default. Levitate
												will automatically find &quot;State&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={addressStateFieldId}
									onChange={onFieldChange(addressStateFieldId)}
									type='text'
									value={parser.stateField}
								/>
							</div>
							<div className={css(styleSheet.inputWrapper)}>
								<label htmlFor={addressPostalCodeFieldId} className={css(styleSheet.text, styleSheet.label)}>
									<div className={css(baseStyleSheet.flex)}>
										Address Zip Code Field
										<HelpTooltip className={css(styleSheet.tooltip)}>
											<div className={css(styleSheet.tooltipContent)}>
												Only supply a value here if Levitate can&apos;t pick up the address zip code by default.
												Levitate will automatically find fields like &quot;Zip&quot;, &quot;Zip Code&quot;, or
												&quot;Postal Code&quot;.
											</div>
										</HelpTooltip>
									</div>
								</label>
								<TextInput
									inputId={addressPostalCodeFieldId}
									onChange={onFieldChange(addressPostalCodeFieldId)}
									type='text'
									value={parser.postalCodeField}
								/>
							</div>
						</>
					)}
					{parserType === ParserType.Custom && (
						<div className={css(styleSheet.inputWrapper)}>
							<Checkbox id={preferHtmlId} onChange={onPreferHtmlChange} checked={parser.preferHtml}>
								<div className={css(baseStyleSheet.flex)}>
									Prefer Html
									<HelpTooltip className={css(styleSheet.tooltip)}>
										<div className={css(styleSheet.tooltipContent)}>
											Levitate will attempt to parse the plain text version of the lead email. This setting will force
											it to parse the HTML formatted version of the lead email. Use caution when enabling.
										</div>
									</HelpTooltip>
								</div>
							</Checkbox>
						</div>
					)}
				</div>
				<div className={css(styleSheet.buttonsContainer, processing && baseStyleSheet.flexCenter)}>
					{processing ? (
						<LoadingSpinner type='small' />
					) : (
						<>
							<div className={css(styleSheet.leftButtonGroup)}>
								<button
									className={css(baseStyleSheet.ctaButton)}
									disabled={!onSave || !parser.name || !parser.tag}
									onClick={onParserSave}
								>
									Save
								</button>
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onParserCancel}>
									Cancel
								</button>
							</div>
							{onDelete && !isNew && (
								<button className={css(baseStyleSheet.ctaButtonReverse, styleSheet.deleteCta)} onClick={onParserDelete}>
									Delete
								</button>
							)}
						</>
					)}
				</div>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: isDeleteConfirmationOpen,
						onRequestClose: onDeleteConfirmationClose,
					}}
					options={deleteConfirmationOptions}
					title={`Are you sure you want to delete the "${parser?.name || parser?.tag}" custom lead parser? This action cannot be undone.`}
				/>
			</div>
		);
	}
);

const CustomLeadParserModalWithContext = inject(ModalChildComponentContextKey)(BaseCustomLeadParserModal);
const CustomLeadParserModalWithLogging = withEventLogging(CustomLeadParserModalWithContext, 'CustomLeadParser');

export const CustomLeadParserModal = asModalComponent(CustomLeadParserModalWithLogging, {
	className: `${idPrefix}, ${css(styleSheet.modal)}`,
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
